export const isBlank = (value?: string | number | null) =>
  value === "" || value == null;

export const hasNumericTaxWithheld = (value?: string | number | null) =>
  !isBlank(value) && Number.isFinite(Number(value));

// The API refuses a tax equal to the gross too, since it leaves no net amount.
const isBelowGross = (
  value: string | number | null | undefined,
  { amount }: { amount?: string | number | null },
) => {
  if (isBlank(value) || isBlank(amount)) {
    return true;
  }
  if (Number(value) === Number(amount)) {
    return "Net amount must be greater than 0";
  }
  return (
    Number(value) < Number(amount) ||
    "Tax withheld cannot exceed the gross amount"
  );
};

export const OPTIONAL_TAX_WITHHELD_RULES = {
  min: { value: 0, message: "Tax withheld cannot be negative" },
  validate: {
    isNumber: (value?: string | number | null) =>
      isBlank(value) ||
      hasNumericTaxWithheld(value) ||
      "Enter a valid tax withheld",
    isBelowGross,
  },
};

export const REQUIRED_TAX_WITHHELD_RULES = {
  ...OPTIONAL_TAX_WITHHELD_RULES,
  required: "Tax withheld is required",
};

// A stored 0 means none was withheld, so it reads as untaxed.
export const isTaxed = (value?: string | number | null) =>
  hasNumericTaxWithheld(value) && Number(value) > 0;

export const amountLabelOf = (
  shouldShowTax: boolean,
  { required = false }: { required?: boolean } = {},
) => {
  const label = shouldShowTax ? "Gross amount" : "Amount";
  return required ? `${label} *` : label;
};

export const clearedWhenBlank = <T extends string | number>(
  value?: T | null,
): T | null => (isBlank(value) ? null : (value as T));

export const netAmountOf = (
  grossAmount?: string | number | null,
  taxWithheld?: string | number | null,
): number | null => {
  if (isBlank(grossAmount) || isBlank(taxWithheld)) {
    return null;
  }
  const net = Number(grossAmount) - Number(taxWithheld);
  return Number.isFinite(net) ? Math.round(net * 100) / 100 : null;
};

export interface GrossAndTax {
  grossAmount: number;
  taxWithheld: number;
}

export const grossAndTaxOf = (
  netAmount?: string | number | null,
  taxRatePercent?: string | number | null,
): GrossAndTax | null => {
  if (isBlank(netAmount) || isBlank(taxRatePercent)) {
    return null;
  }
  const netCents = Math.round(Number(netAmount) * 100);
  const rate = Number(taxRatePercent);
  const hasValidNet = Number.isFinite(netCents) && netCents > 0;
  const hasValidRate = rate >= 0 && rate < 100;
  if (!hasValidNet || !hasValidRate) {
    return null;
  }
  // Tax is the remainder of the rounded gross, so gross minus tax is exactly the net paid.
  const grossCents = Math.round(netCents / (1 - rate / 100));
  return {
    grossAmount: grossCents / 100,
    taxWithheld: (grossCents - netCents) / 100,
  };
};
